// +----------------------------------------------------------------------
// | EB [ EB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016~2024 https://www.eb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed EB并不是自由软件，未经许可不能去掉EB相关版权
// +----------------------------------------------------------------------
// | Author: EB Team <admin@eb.com>
// +----------------------------------------------------------------------

import { useRoute, useRouter } from 'vue-router'

/**
 * cursor 添加 代替全部
 * FormCreate 辅助对象由 fc-frame 写到 iframe 的 form_create_helper（EB 原名）。
 * 自建弹层写的是 window / parent.formCreateHelper。弹窗页两种都读。
 */
export interface FormCreateData {
  [key: string]: any
}

/**
 * FormCreate 回调函数类型
 */
export type FormCreateCallback = () => void

/**
 * FormCreate 辅助 Composable
 * 用于在弹窗页面中传递数据
 * 
 * @example
 * ```ts
 * const { submitAndClose } = useFormCreateHelper()
 * 
 * const handleSelect = (data) => {
 *   submitAndClose({ id: data.id, name: data.name })
 * }
 * ```
 */
export function useFormCreateHelper() {
  const route = useRoute()
  const router = useRouter()

  /**
   * 获取 FormCreate 辅助对象
   * iframe 内优先读本窗口（fc-frame 写在 contentWindow 上），再读父窗口（自建选图弹层）
   */
  const getHelper = (): any => {
    if (typeof window === 'undefined') return null
    const read = (win: Window | null | undefined) => {
      if (!win) return null
      const w = win as Window & { form_create_helper?: any; formCreateHelper?: any }
      return w.form_create_helper || w.formCreateHelper || null
    }
    const self = read(window)
    if (self) return self
    try {
      if (window.parent && window.parent !== window) return read(window.parent)
    } catch {
      // 父窗口跨域
    }
    return null
  }

  /**
   * 设置字段值
   * @param field 字段名
   * @param value 字段值
   */
  const setFieldValue = (field: string, value: FormCreateData): void => {
    const helper = getHelper()
    if (helper) {
      helper.set(field, value)
    } else {
      console.warn('[useFormCreateHelper] formCreateHelper is not available')
    }
  }

  /**
   * 获取字段值
   * @param field 字段名
   * @returns 字段值
   */
  const getFieldValue = (field: string): FormCreateData | null => {
    const helper = getHelper()
    if (helper) {
      return helper.get(field)
    }
    return null
  }

  /**
   * 关闭字段对应的弹窗
   * @param field 字段名
   */
  const closeField = (field: string): void => {
    const helper = getHelper()
    if (helper) {
      helper.close(field)
    }
  }

  /**
   * 获取当前路由的字段名
   * @returns 字段名
   */
  const getCurrentField = (): string => {
    return (route.query.field as string) || ''
  }

  /**
   * 提交数据并关闭弹窗
   * @param value 要提交的数据
   * @param field 字段名，不传则使用路由参数
   */
  const submitAndClose = (value: FormCreateData, field?: string): void => {
    const fieldName = field || getCurrentField()
    
    if (!fieldName) {
      console.warn('[useFormCreateHelper] field name is required')
      return
    }

    setFieldValue(fieldName, value)
    closeField(fieldName)
  }

  /**
   * 检查 FormCreate 辅助对象是否可用
   * @returns 是否可用
   */
  const isAvailable = (): boolean => {
    return getHelper() !== null
  }

  /**
   * 取消并返回（不提交数据）
   */
  const cancel = (): void => {
    router.back()
  }

  /**
   * 注册确定按钮的回调函数
   * @param callback 回调函数
   */
  const onOk = (callback: FormCreateCallback): void => {
    const helper = getHelper() as any
    if (helper && typeof helper.onOk === 'function') {
      helper.onOk(callback)
    }
  }

  /**
   * 清除字段值
   * @param field 字段名
   */
  const clearField = (field?: string): void => {
    const helper = getHelper() as any
    const fieldName = field || getCurrentField()
    
    if (helper && typeof helper.clear === 'function') {
      helper.clear(fieldName)
    }
  }

  return {
    setFieldValue,
    getFieldValue,
    closeField,
    getCurrentField,
    submitAndClose,
    isAvailable,
    cancel,
    onOk,
    clearField
  }
}

